import { Plus } from 'iconoir-react'
import moment from 'moment'
import { type MouseEvent, type ReactNode, useMemo, useState } from 'react'
import { Calendar, type Event, momentLocalizer, Views } from 'react-big-calendar'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import { Button } from 'app/components/ui/button'
import { DAY_KEY } from 'lib/taskCalendar'
import { dueDateAsDay } from 'app/lib/taskDueDate'
import type { TaskJson } from 'app/types/entities'
import { TaskCalendarChip } from './TaskCalendarChip'

type TaskEvent = Event & { resource: TaskJson }

const localizer = momentLocalizer(moment)
// Google's unpadded day numbers; the library defaults to "01".
const FORMATS = { dateFormat: `D` }
const DragAndDropCalendar = withDragAndDrop<TaskEvent>(Calendar)

/** Only the hovered day's New Task button creates a task; a click elsewhere in a day never does. */
export function TaskCalendarMonth({
  anchor,
  tasks,
  canDrag,
  onOpenTask,
  onToggleStatus,
  onReschedule,
  onCreate,
  onOpenDay,
}: {
  anchor: moment.Moment
  tasks: TaskJson[]
  canDrag: boolean
  onOpenTask: (task: TaskJson) => void
  onToggleStatus: (task: TaskJson) => void
  onReschedule: (task: TaskJson, day: Date) => void
  onCreate: (day: Date) => void
  onOpenDay: (day: Date) => void
}) {
  const events = useMemo<TaskEvent[]>(
    () =>
      tasks.flatMap((task) => {
        if (!task.dueDate) return []
        const day = dueDateAsDay(task.dueDate).toDate()
        return [{ title: task.title, start: day, end: day, allDay: true, resource: task }]
      }),
    [tasks],
  )

  const [hoveredDay, setHoveredDay] = useState<string>()

  // A day's background cell sits under its tasks and date number, so the
  // pointer is resolved through every layer at that point, not the top one.
  const trackHoveredDay = (e: MouseEvent) => {
    const cell = document.elementsFromPoint(e.clientX, e.clientY).find((el) => el.classList.contains(`rbc-day-bg`))
    setHoveredDay(cell?.closest(`[data-day]`)?.getAttribute(`data-day`) ?? undefined)
  }

  const components = useMemo(
    () => ({
      dateCellWrapper: ({ value, children }: { value: Date; children?: ReactNode }) => (
        <div data-day={moment(value).format(DAY_KEY)} className="contents">
          {children}
        </div>
      ),
      month: {
        dateHeader: ({ label, date }: { label: string; date: Date }) => (
          <div className="flex items-center justify-between gap-1 text-ui-xs">
            <Button variant="ghost" size="inline" data-day-number onClick={() => onOpenDay(date)}>
              {label}
            </Button>
            {hoveredDay === moment(date).format(DAY_KEY) && (
              <Button variant="subtle" size="inline" onClick={() => onCreate(date)}>
                <Plus />
                New Task
              </Button>
            )}
          </div>
        ),
      },
      event: ({ event }: { event: TaskEvent }) => (
        <TaskCalendarChip
          task={event.resource}
          variant="compact"
          onOpen={() => onOpenTask(event.resource)}
          onToggleStatus={() => onToggleStatus(event.resource)}
        />
      ),
    }),
    [onOpenTask, onToggleStatus, onOpenDay, onCreate, hoveredDay],
  )

  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      onMouseMove={trackHoveredDay}
      onMouseLeave={() => setHoveredDay(undefined)}
    >
      <DragAndDropCalendar
        localizer={localizer}
        events={events}
        date={anchor.toDate()}
        view={Views.MONTH}
        views={[Views.MONTH]}
        toolbar={false}
        formats={FORMATS}
        popup
        resizable={false}
        draggableAccessor={() => canDrag}
        // Controlled from the URL; the toolbar above owns navigation.
        onNavigate={() => undefined}
        onView={() => undefined}
        onDrillDown={onOpenDay}
        onEventDrop={({ event, start }) => {
          if (!moment(start).isSame(event.start, `day`)) onReschedule(event.resource, moment(start).toDate())
        }}
        components={components}
      />
    </div>
  )
}
